// SPDX-License-Identifier: MIT
// « Le double au propre » — indicateur de sauvegarde cloud du CODE (desktop only).
// Sœur visuelle de ink-gauge : même langage carnet/encre. Le code construit est
// recopié dans le repo Forgejo du projet pour SUIVRE l'user d'un poste à l'autre.
// Métaphore : un double recopié (deux feuillets) qu'on classe à l'abri. Ruban
// discret pour les états calmes (sauvegardé / en cours / pas encore), carte
// bordée pour la divergence (le seul état qui mérite qu'on s'arrête) — exactement
// le parallèle ruban/carte de la jauge. Honnêteté §4/§6 : « pas encore sauvegardé »
// est NEUTRE (jamais alarmant), et la divergence est un AVERTISSEMENT, jamais un
// écrasement (« rien n'est écrasé »).
import { t } from '../i18n/t';
import type { Locale } from '../i18n/detect';
import type { CloudSaveState } from '../projects/cloud-save';

// Deux feuillets recopiés (le « double »). Ligne claire, cohérente avec la plume.
const SHEETS =
  '<svg class="cloud-save__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="2.2" y="2.2" width="9" height="11.5" rx="1.6" stroke="currentColor" stroke-width="1.3" fill="color-mix(in srgb,currentColor 10%,transparent)"/>' +
  '<rect x="6.8" y="4.3" width="9" height="11.5" rx="1.6" stroke="currentColor" stroke-width="1.3" fill="var(--cream)"/></svg>';
// Variante « classé » : le double + un coche d'encre sur le feuillet de devant.
const SHEETS_CHECK =
  '<svg class="cloud-save__glyph" viewBox="0 0 18 18" fill="none" aria-hidden="true">' +
  '<rect x="2.2" y="2.2" width="9" height="11.5" rx="1.6" stroke="currentColor" stroke-width="1.3" fill="color-mix(in srgb,currentColor 10%,transparent)"/>' +
  '<rect x="6.8" y="4.3" width="9" height="11.5" rx="1.6" stroke="currentColor" stroke-width="1.3" fill="var(--cream)"/>' +
  '<path d="M8.7 10.1 10.5 12l3-3.3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>';
// Carte divergence : deux feuillets qui s'écartent (les deux postes ont divergé).
const SHEETS_SPLIT =
  '<svg class="cloud-save-card__glyph" viewBox="0 0 30 30" fill="none" aria-hidden="true">' +
  '<rect x="2.5" y="6" width="13" height="17" rx="2" transform="rotate(-7 9 14.5)" stroke="currentColor" stroke-width="1.6" fill="color-mix(in srgb,currentColor 9%,transparent)"/>' +
  '<rect x="14.5" y="6" width="13" height="17" rx="2" transform="rotate(7 21 14.5)" stroke="currentColor" stroke-width="1.6" fill="var(--paper-warm)"/></svg>';

const HUE_VAR: Record<'sage' | 'orange' | 'muted', string> = {
  sage: 'var(--sage-deep)', orange: 'var(--orange-deep)', muted: 'var(--muted)',
};

export interface CloudSaveIndicatorOptions {
  locale?: Locale;
  onSave?: () => void;     // « Sauvegarder maintenant » (état unsaved)
  onRecover?: () => void;  // « Récupérer la version cloud » (état diverged)
}

// Rend l'indicateur pour un état. `idle` (avant toute sauvegarde) → élément vide
// et masqué : le slot reste inerte tant qu'aucun run n'a écrit. La divergence
// rend une CARTE ; les autres états, un RUBAN inline.
export function renderCloudSaveIndicator(state: CloudSaveState, opts: CloudSaveIndicatorOptions = {}): HTMLElement {
  if (state === 'diverged') return renderDivergedCard(opts);
  return renderRibbon(state, opts);
}

function renderRibbon(state: Exclude<CloudSaveState, 'diverged'>, opts: CloudSaveIndicatorOptions): HTMLElement {
  const el = document.createElement('span');
  el.className = 'cloud-save';
  el.dataset.component = 'cloud-save';
  el.dataset.state = state;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');

  if (state === 'idle') { el.hidden = true; return el; }

  const hue = state === 'saved' ? 'sage' : 'muted';
  el.style.setProperty('--hue', HUE_VAR[hue]);
  el.classList.add(`cloud-save--${state}`);

  const glyph = document.createElement('span');
  glyph.className = 'cloud-save__glyph-host';
  glyph.innerHTML = state === 'saved' ? SHEETS_CHECK : SHEETS;
  el.appendChild(glyph.firstElementChild as SVGElement);

  const label = document.createElement('span');
  label.className = 'cloud-save__label';
  label.textContent = t(`cloudSave.${state}`);
  el.appendChild(label);

  if (state === 'saved') {
    const hand = document.createElement('span');
    hand.className = 'cloud-save__hand';
    hand.textContent = t('cloudSave.savedHand');
    el.appendChild(hand);
  }
  if (state === 'unsaved') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cloud-save__btn';
    btn.dataset.component = 'cloud-save-now';
    btn.textContent = t('cloudSave.saveNow');
    btn.addEventListener('click', () => opts.onSave?.());
    el.appendChild(btn);
  }
  return el;
}

function renderDivergedCard(opts: CloudSaveIndicatorOptions): HTMLElement {
  const card = document.createElement('div');
  card.className = 'cloud-save-card';
  card.dataset.component = 'cloud-save';
  card.dataset.state = 'diverged';
  card.setAttribute('role', 'alert');

  const row = document.createElement('div');
  row.className = 'cloud-save-card__row';
  const glyph = document.createElement('span');
  glyph.className = 'cloud-save-card__glyph-host';
  glyph.innerHTML = SHEETS_SPLIT;
  row.appendChild(glyph.firstElementChild as SVGElement);

  const col = document.createElement('div');
  const title = document.createElement('div');
  title.className = 'cloud-save-card__title';
  title.textContent = t('cloudSave.diverged.title');
  const sub = document.createElement('p');
  sub.className = 'cloud-save-card__sub';
  sub.textContent = t('cloudSave.diverged.sub');
  col.append(title, sub);
  row.appendChild(col);
  card.appendChild(row);

  const actions = document.createElement('div');
  actions.className = 'cloud-save-card__actions';
  const recover = document.createElement('button');
  recover.type = 'button';
  recover.className = 'cloud-save-btn cloud-save-btn--primary';
  recover.dataset.component = 'cloud-save-recover';
  recover.textContent = t('cloudSave.diverged.recover');
  recover.addEventListener('click', () => opts.onRecover?.());
  actions.appendChild(recover);
  card.appendChild(actions);

  const hand = document.createElement('p');
  hand.className = 'cloud-save-card__hand';
  hand.textContent = t('cloudSave.diverged.hand');
  card.appendChild(hand);

  const note = document.createElement('p');
  note.className = 'cloud-save-card__note';
  note.textContent = t('cloudSave.diverged.note');
  card.appendChild(note);

  return card;
}
